import React, { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import type { OfferListSortOption } from 'constants/offerListFilters'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import type { TourSearchFilterContentElement } from 'components/Tours/TourSearchFilterContent'
import TourSearchFilterContent from 'components/Tours/TourSearchFilterContent'
import { EmptyArray } from 'lib/array/arrayUtils'

interface Option {
  value: string
  label?: string
  count: number
}

export function generateOptionItems(
  options: Array<Option>,
  localStates: Set<string>,
) {
  return options.map(({ value, label, count }) => ({
    label: (
      <Group direction="horizontal" horizontalAlign="space-between">
        {label ?? value}
        <span>({count})</span>
      </Group>
    ),
    value,
    checked: localStates.has(value),
  }))
}

interface RangeFilter {
  min: string | undefined
  max: string | undefined
}

export type TourSearchPageFilterSubmit = {
  operators: Array<string>
  categories: Array<string>
  productTypes: Array<string>
  price: { start: number, end: number }
  duration: { start: number, end: number }
  numberOfPlacesVisited?: RangeFilter
  destinations: Array<string>
  groupTypes: Array<string>
  offerTags: Array<string>
  luxPlusFeatures: Array<string>
  agentHubFeatures: Array<string>
  sortBy?: string
  tourStyles?: Array<string>
  campaigns?: Array<string>
}

interface Props {
  open?: boolean
  onClose?: () => void
  onFilterSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  searchFacetsState?: App.StatefulData<App.Tours.TourSearchFacets>
  initialFacets?: App.Tours.TourSearchFacets
  existingFilters?: App.OfferListFilters
  sortOptions?: Array<OfferListSortOption>
}

function TourSearchPageFilters({
  open,
  onClose,
  existingFilters,
  onFilterSubmit,
  sortOptions = EmptyArray,
  searchFacetsState,
  initialFacets,
}: Props) {
  const intl = useIntl()
  const tourSearchFilter = useRef<TourSearchFilterContentElement>(null)

  const onFormReset = useCallback(() => {
    tourSearchFilter.current?.onFormReset()
  }, [tourSearchFilter])

  const sortAndFilterText = intl.formatMessage(
    {
      id: 'L2POWQ37Ak',
      defaultMessage: 'Sort & Filter',
      description: 'Modal header title when sort options are available alongside filters',
    },
  )
  const filtersText = intl.formatMessage(
    {
      id: '0INZOW1nF3',
      defaultMessage: 'Filters',
      description: 'Modal header title when only filter options are available',
    },
  )
  const sortAndFiltersText =
    sortOptions && sortOptions.length > 0 ? sortAndFilterText : filtersText

  const viewOffersText = intl.formatMessage(
    {
      id: 'V8ocERkC/G',
      defaultMessage: 'View offers',
      description: 'Primary action button to apply filters and view matching tour offers',
    },
  )
  const resetAllText = intl.formatMessage(
    {
      id: 'sU2q7cBdoA',
      defaultMessage: 'Reset all',
      description: 'Secondary action button to clear all applied filters',
    },
  )

  return (
    <ModalBase mode="drawer" isOpen={!!open} onClose={onClose}>
      <form onSubmit={onFilterSubmit}>
        <ModalHeader title={sortAndFiltersText} onCloseButtonClick={onClose} />
        <ModalBody>
          <ModalContent>
            <TourSearchFilterContent
              ref={tourSearchFilter}
              modalView
              searchFacetsState={searchFacetsState}
              existingFilters={existingFilters}
              initialFacets={initialFacets}
              sortOptions={sortOptions}
            />
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: viewOffersText,
            type: 'submit',
            'data-testid': 'view-offers-filter-button',
          }}
          secondaryActionProps={{
            children: resetAllText,
            onClick: onFormReset,
          }}
        />
      </form>
    </ModalBase>
  )
}

export default TourSearchPageFilters
